import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const NotificationsContext = createContext(null);
const POLL_MS = 30000;

export function NotificationsProvider({ children }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread_count");
      setUnread(data.count);
    } catch {
      /* transient — the next poll will resync */
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 100 } });
      setItems(data);
    } catch {
      /* keep whatever is on screen */
    }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [loadCount]);

  /** Optimistic update that rolls back and resyncs if the server rejects it. */
  const mutate = useCallback(
    async (apply, request, failureMessage) => {
      const rollback = { items, unread };
      apply();
      try {
        const { data } = await request();
        if (typeof data?.unread === "number") setUnread(data.unread);
      } catch {
        setItems(rollback.items);
        setUnread(rollback.unread);
        toast.error(failureMessage);
        loadItems();
        loadCount();
      }
    },
    [items, unread, loadItems, loadCount]
  );

  const markRead = useCallback(
    (id) =>
      mutate(
        () => {
          setItems((xs) => xs.map((x) => (x.id === id ? { ...x, read: true } : x)));
          setUnread((c) => Math.max(0, c - 1));
        },
        () => api.post(`/notifications/${id}/read`),
        "Couldn't mark that as read — try again"
      ),
    [mutate]
  );

  const markAll = useCallback(
    () =>
      mutate(
        () => {
          setItems((xs) => xs.map((x) => ({ ...x, read: true })));
          setUnread(0);
        },
        () => api.post("/notifications/read_all"),
        "Couldn't mark everything as read — try again"
      ),
    [mutate]
  );

  const remove = useCallback(
    (id, wasRead) =>
      mutate(
        () => {
          setItems((xs) => xs.filter((x) => x.id !== id));
          if (!wasRead) setUnread((c) => Math.max(0, c - 1));
        },
        () => api.delete(`/notifications/${id}`),
        "Couldn't dismiss that notification — it's still there"
      ),
    [mutate]
  );

  const clearRead = useCallback(
    () =>
      mutate(
        () => setItems((xs) => xs.filter((x) => !x.read)),
        () => api.post("/notifications/clear_read"),
        "Couldn't clear those notifications — try again"
      ),
    [mutate]
  );

  const clearAll = useCallback(
    () =>
      mutate(
        () => {
          setItems([]);
          setUnread(0);
        },
        () => api.post("/notifications/clear_all"),
        "Couldn't clear your notifications — try again"
      ),
    [mutate]
  );

  return (
    <NotificationsContext.Provider
      value={{ items, unread, loadItems, loadCount, markRead, markAll, remove, clearRead, clearAll }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationsContext);
